import { Link } from 'react-router-dom';
import { type AutopilotTaskDto, useAssignTaskGroupToUser } from '@urbanx/agx.activities.client';
import { AgencyModel } from '@urbanx/agx.agencies.client';
import {
  AgxBadge,
  AgxModal,
  AgxSpacer,
  AgxTable,
  AgxTypography,
  IColumnConfig,
  SelectChipOption,
} from '@urbanx/agx.ui-components2';
import TaskAgencyCell from 'pages/allTasks/Cells/TaskAgencyCell';
import LeadAgentCell from 'components/LeadAgentCell';
import { useLeadAgentFilter } from 'hooks/useLeadAgentFilter';
import TaskAssigneeCell from 'pages/allTasks/Cells/TaskAssigneeCell';
import TaskCampaignCell from 'pages/allTasks/Cells/TaskCampaignCell';
import { TaskCell } from 'pages/allTasks/Cells/TaskCell';
import TaskDueCell from 'pages/allTasks/Cells/TaskDueCell';
import TaskStatusCell from 'pages/allTasks/Cells/TaskStatusCell';
import useTaskSearch from 'pages/allTasks/useTaskSearch';
import { useCallback, useEffect, useEffectEvent, useMemo, useRef, useState } from 'react';
import { getTaskLink } from 'utils/tasks/useNavigateToTask';
import { getTaskDashboardLabel, getTaskDisplayGroups } from 'utils/tasks';
import { useWorkflowConcurrencyError } from 'hooks/useWorkflowConcurrencyError';
import { createTaskFilterState } from './CreateTaskFilterState';
import { TasksSearchFilterType } from './TasksSearchFilterType';

interface TaskTableProps {
  assignedToUserIds?: string[];
  selectedAgencies?: string[];
  searchFilterType: TasksSearchFilterType;
  agencies: AgencyModel[];
  assigneeOptions: SelectChipOption[];
  isModalOpen: boolean;
  setIsModalOpen: (isOpen: boolean) => void;
  filtersPending?: boolean;
  hasNoMatchingAgencies?: boolean;
}

const tableColumns: (string | IColumnConfig)[] = [
  'Task',
  'Agency',
  'Lead Agent',
  'Campaign',
  { name: 'Assignee', disableRowLink: true, align: 'center' },
  'Status',
  { name: 'Due', align: 'right' },
];

const TasksModal = ({
  assignedToUserIds,
  selectedAgencies,
  searchFilterType,
  agencies,
  assigneeOptions,
  isModalOpen,
  setIsModalOpen,
  filtersPending,
  hasNoMatchingAgencies,
}: TaskTableProps) => {
  const { agentLookup } = useLeadAgentFilter({});
  const taskFilterState = useMemo(
    () => createTaskFilterState(searchFilterType, assignedToUserIds, selectedAgencies),
    [searchFilterType, assignedToUserIds, selectedAgencies]
  );
  const [searchPreparedForOpen, setSearchPreparedForOpen] = useState(false);

  const { tasks, searchAllTasksPending, totalCount, hasMore, loadMore, restartSearchInBackground } =
    useTaskSearch(
      taskFilterState,
      filtersPending,
      hasNoMatchingAgencies,
      !isModalOpen || !searchPreparedForOpen
    );
  const assignmentWorkflowRef = useRef<AutopilotTaskDto | undefined>(undefined);
  const handleWorkflowConcurrencyError = useWorkflowConcurrencyError();
  const taskGroups = useMemo(() => getTaskDisplayGroups(tasks), [tasks]);
  const displayTasks = useMemo(() => taskGroups.map((group) => group.displayTask), [taskGroups]);
  const { assignTaskGroupToUser, assignTaskGroupToUserPending } = useAssignTaskGroupToUser({
    onSuccess: () => {
      void restartSearchInBackground();
    },
    onError: (error) => {
      if (!assignmentWorkflowRef.current) return;

      void handleWorkflowConcurrencyError(error, assignmentWorkflowRef.current, {
        refresh: restartSearchInBackground,
      });
    },
  });

  const onAssignTaskToUser = useCallback(
    async (userId?: string, taskId?: string) => {
      if (!userId || !taskId || assignTaskGroupToUserPending) return;

      const taskGroup = taskGroups.find((group) => group.displayTask.id === taskId);
      if (!taskGroup) return;

      assignmentWorkflowRef.current = taskGroup.displayTask;

      await assignTaskGroupToUser({
        taskId,
        userId,
      });
    },
    [assignTaskGroupToUserPending, assignTaskGroupToUser, taskGroups]
  );

  const [lastRowElement, setLastRowElement] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isModalOpen) return;

    let cancelled = false;

    const prepareSearch = async () => {
      await restartSearchInBackground();
      if (!cancelled) setSearchPreparedForOpen(true);
    };

    void prepareSearch();

    return () => {
      cancelled = true;
      setSearchPreparedForOpen(false);
    };
  }, [isModalOpen, restartSearchInBackground]);

  // The disabled restart truncates the cache but cannot refetch it. Restart again after the
  // observer is active so its active-only refetch is the sole request made for the reopen.
  useEffect(() => {
    if (!isModalOpen || !searchPreparedForOpen) return;

    void restartSearchInBackground();
  }, [isModalOpen, restartSearchInBackground, searchPreparedForOpen]);

  const lastRowRef = useCallback((node: HTMLDivElement | null) => {
    setLastRowElement(node);
  }, []);

  const loadMoreWhenVisible = useEffectEvent(() => {
    if (!hasMore || searchAllTasksPending) return;

    loadMore();
  });

  useEffect(() => {
    if (!lastRowElement || !isModalOpen) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMoreWhenVisible();
      },
      {
        root: null,
        rootMargin: '0px 0px 300px 0px',
        threshold: 0,
      }
    );

    observer.observe(lastRowElement);

    return () => observer.disconnect();
  }, [isModalOpen, lastRowElement]);

  const rows = useMemo(() => {
    return displayTasks.map((task, idx) => {
      const isNew = !!task.isNew;
      const isLastRow = idx === displayTasks.length - 1;

      return {
        Task: (
          <div ref={isLastRow ? lastRowRef : null}>
            <TaskCell task={task} index={idx} isNew={isNew} />
          </div>
        ),
        Agency: <TaskAgencyCell task={task} agencies={agencies ?? []} index={idx} isNew={isNew} />,
        'Lead Agent': (
          <LeadAgentCell
            id={`task-${task.id}-lead-agent`}
            agent={task.leadAgentId ? agentLookup[task.leadAgentId] : undefined}
            truncateLabels
          />
        ),
        Campaign: (
          <TaskCampaignCell
            propertyAddress={task.propertyAddress ?? 'Unknown Address'}
            index={idx}
            isNew={isNew}
          />
        ),
        Assignee: (
          <div onClick={(e) => e.stopPropagation()}>
            <TaskAssigneeCell
              task={task}
              assigneeOptions={assigneeOptions}
              onAssignTask={onAssignTaskToUser}
            />
          </div>
        ),
        Status: <TaskStatusCell task={task} />,
        Due: <TaskDueCell task={task} />,
      };
    });
  }, [displayTasks, agencies, agentLookup, assigneeOptions, lastRowRef, onAssignTaskToUser]);

  return (
    <AgxModal
      type={'fullWidth'}
      variant={'withoutDivider'}
      actionButtonContainerStyle={'withDivider'}
      secondaryTitle={
        <AgxSpacer direction="horizontal" gap={'2xs'}>
          <AgxTypography
            id="agx-modal-title"
            size="medium"
            type="body_ui_strong"
            dataTestId={`agx-modal-${searchFilterType.toString()}`}
          >
            {`Today's ${searchFilterType.toString()}`}
          </AgxTypography>
          <AgxBadge
            type="colourfill"
            colour="grey"
            size="sm"
            text={totalCount?.toString() ?? '--'}
          />
        </AgxSpacer>
      }
      content={
        <AgxSpacer
          direction="vertical"
          fill
          contentAlignment="center"
          contentJustification="center"
          padding={{ top: 'md', bottom: 'md', left: 'xl', right: 'xl' }}
        >
          <AgxTable
            variant="default"
            tableStyle="rounded"
            columns={tableColumns}
            rows={rows}
            renderRowLink={(rowIndex, linkProps) => {
              const task = displayTasks[rowIndex];
              const to = getTaskLink(task);
              return to ? (
                <Link
                  {...linkProps}
                  to={to}
                  aria-label={
                    getTaskDashboardLabel(task).trim() ||
                    task.displayTitle?.trim() ||
                    (task.id ? `Task ${task.id}` : 'Open task')
                  }
                />
              ) : undefined;
            }}
            showLoadingState={searchAllTasksPending && tasks.length === 0}
            noDataGlyphType="checklistSmile"
            noDataLabel="No task found."
          />
        </AgxSpacer>
      }
      setIsDialogOpen={setIsModalOpen}
      isOpen={isModalOpen}
    />
  );
};

export default TasksModal;
